import React, { useState } from "react";
import { Mail, MapPin, Phone, Send, CheckCircle, Zap } from "../components/Icons";
import { Reveal, SectionHeading, PageGlow, Card } from "../components/ui";

const BUDGET_OPTIONS = [
  "Under ₹5 Lakhs",
  "₹5L – ₹15L",
  "₹15L – ₹40L",
  "₹40L – ₹1 Crore",
  "₹1 Crore+",
  "Let's discuss",
];

const PLATFORM_OPTIONS = [
  "Android App",
  "iOS App",
  "macOS Application",
  "Web Platform",
  "Enterprise Software",
  "Multiple Platforms",
];

const CONTACT_INFO = [
  { icon: Mail,   label: "Primary Email",   value: "contact@dowble.com",                    href: "mailto:contact@dowble.com" },
  { icon: Phone,  label: "Phone",           value: "+91 9036959940",                        href: "tel:+919036959940" },
  { icon: MapPin, label: "Office Location", value: "Sarjapur, Bengaluru, Karnataka, India", href: null },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    platform: "",
    budget: "",
    brief: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.brief) return;

    setLoading(true);

    try {
      const response = await fetch("https://dowble-mail-worker.ram-tulugu.workers.dev", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitted(true);
      } else {
        alert(result.error || "Submission failed. Please try again.");
      }
    } catch (error) {
      console.error("Form error:", error);
      alert("Network error. Unable to reach submission server.");
    } finally {
      setLoading(false);
    }
  };

  const emailValid = form.email === "" || EMAIL_RE.test(form.email);
  const canSubmit = form.name && form.email && EMAIL_RE.test(form.email) && form.brief;

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative py-20 px-6 overflow-hidden">
        <PageGlow />
        <div className="relative max-w-4xl mx-auto text-center">
          <Reveal>
            <SectionHeading
              eyebrow="Launch Project"
              title={<>Let's Build Something <span className="text-gradient-brand">Extraordinary.</span></>}
              subtitle="Tell us your idea. We'll respond within 4 hours with an initial assessment and suggested next steps — no commitment required."
            />
          </Reveal>
        </div>
      </section>

      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-10">

          {/* Contact Info sidebar */}
          <Reveal className="lg:col-span-1 space-y-5">
            <div>
              <h2 className="text-zinc-900 font-bold text-lg mb-1">Get in Touch</h2>
              <p className="text-zinc-600 text-sm">We're based in Bengaluru but work with clients globally.</p>
            </div>

            {CONTACT_INFO.map((info) => {
              const Icon = info.icon;
              return (
                <Card key={info.label} interactive className="flex items-start gap-4 p-5">
                  <span className="w-10 h-10 rounded-xl bg-accent-500/10 border border-accent-500/20 flex items-center justify-center text-accent-600 flex-shrink-0">
                    <Icon size={18} />
                  </span>
                  <div>
                    <p className="text-zinc-500 text-xs mb-0.5">{info.label}</p>
                    {info.href ? (
                      <a href={info.href} className="text-zinc-900 font-semibold text-sm hover:text-accent-600 transition-colors">
                        {info.value}
                      </a>
                    ) : (
                      <p className="text-zinc-900 font-semibold text-sm">{info.value}</p>
                    )}
                  </div>
                </Card>
              );
            })}

            {/* Response time promise */}
            <div className="p-5 rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.06]">
              <div className="flex items-center gap-2 mb-2">
                <Zap size={16} className="text-emerald-600" />
                <p className="text-emerald-600 text-xs font-bold uppercase tracking-wider">Response Guarantee</p>
              </div>
              <p className="text-zinc-600 text-sm leading-relaxed">
                Every inbound inquiry receives a substantive response within <strong className="text-zinc-900">4 business hours</strong>.
                Not an autoresponder — a real response from a real engineer who has reviewed your brief.
              </p>
            </div>

            {/* What to expect */}
            <Card className="p-5">
              <p className="text-zinc-900 font-bold text-sm mb-4">What happens next?</p>
              <ul className="space-y-3">
                {[
                  "We review your brief and form an initial technical assessment",
                  "We respond with clarifying questions or a draft scope",
                  "We schedule a 45-minute discovery call",
                  "You receive a fixed-price proposal within 48 hours of the call",
                ].map((step, i) => (
                  <li key={i} className="flex gap-3 text-zinc-600 text-xs">
                    <span className="w-5 h-5 rounded-full bg-zinc-100 text-zinc-600 flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {i + 1}
                    </span>
                    {step}
                  </li>
                ))}
              </ul>
            </Card>
          </Reveal>

          {/* Form */}
          <div className="lg:col-span-2">
            {submitted ? (
              <Reveal className="h-full flex flex-col items-center justify-center text-center py-20 px-8 rounded-3xl border border-emerald-500/20 bg-emerald-500/[0.06] animate-scale-in">
                <span className="inline-flex w-16 h-16 rounded-2xl bg-emerald-500/12 border border-emerald-500/25 items-center justify-center text-emerald-600 mb-6">
                  <CheckCircle size={36} />
                </span>
                <h2 className="text-2xl font-bold tracking-tight text-zinc-900 mb-3">Message Received.</h2>
                <p className="text-zinc-600 text-base max-w-md">
                  Thank you, <strong className="text-zinc-900">{form.name}</strong>. We've received your project brief
                  and will respond to <strong className="text-zinc-900">{form.email}</strong> within 4 hours with
                  our initial assessment.
                </p>
                <div className="mt-8 px-6 py-3 rounded-xl border border-zinc-200 bg-zinc-50">
                  <p className="text-zinc-600 text-xs">Reference: DWB-{Date.now().toString(36).toUpperCase()}</p>
                </div>
              </Reveal>
            ) : (
              <Reveal>
                <form
                  onSubmit={handleSubmit}
                  className="rounded-3xl border border-zinc-200 bg-white backdrop-blur-sm p-8 space-y-6"
                  aria-label="Project inquiry form"
                >
                  <div>
                    <h2 className="text-zinc-900 font-bold text-xl mb-1 tracking-tight">Start Your Project</h2>
                    <p className="text-zinc-600 text-sm">Fields marked <span className="text-rose-500">*</span> are required.</p>
                  </div>

                  {/* Name + Email row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="name" className="field-label">
                        Full Name <span className="text-rose-500">*</span>
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={form.name}
                        onChange={handleChange}
                        placeholder="Jane Doe"
                        className="field-input"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="field-label">
                        Email Address <span className="text-rose-500">*</span>
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={form.email}
                        onChange={handleChange}
                        placeholder="jane@company.com"
                        aria-invalid={!emailValid}
                        className={"field-input " + (!emailValid ? "border-rose-500/60 focus:border-rose-500/60 focus:ring-rose-500/25" : "")}
                      />
                      {!emailValid && (
                        <p className="text-rose-500 text-xs mt-1.5">Please enter a valid email address.</p>
                      )}
                    </div>
                  </div>

                  {/* Company */}
                  <div>
                    <label htmlFor="company" className="field-label">Company / Organization</label>
                    <input
                      id="company"
                      name="company"
                      type="text"
                      value={form.company}
                      onChange={handleChange}
                      placeholder="Your company name (optional)"
                      className="field-input"
                    />
                  </div>

                  {/* Platform + Budget row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="platform" className="field-label">Platform Type</label>
                      <div className="relative">
                        <select
                          id="platform"
                          name="platform"
                          value={form.platform}
                          onChange={handleChange}
                          className="field-input appearance-none pr-10 cursor-pointer"
                        >
                          <option value="">Select platform...</option>
                          {PLATFORM_OPTIONS.map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                        <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-500">▾</span>
                      </div>
                    </div>
                    <div>
                      <label htmlFor="budget" className="field-label">Estimated Budget</label>
                      <div className="relative">
                        <select
                          id="budget"
                          name="budget"
                          value={form.budget}
                          onChange={handleChange}
                          className="field-input appearance-none pr-10 cursor-pointer"
                        >
                          <option value="">Select budget range...</option>
                          {BUDGET_OPTIONS.map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                        <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-500">▾</span>
                      </div>
                    </div>
                  </div>

                  {/* Project Brief */}
                  <div>
                    <label htmlFor="brief" className="field-label">
                      Project Brief <span className="text-rose-500">*</span>
                    </label>
                    <textarea
                      id="brief"
                      name="brief"
                      rows={6}
                      required
                      value={form.brief}
                      onChange={handleChange}
                      placeholder="Describe your project: what does it do, who uses it, what problem does it solve? Include any technical constraints, existing systems to integrate with, or specific requirements you have. The more context, the faster we can assess."
                      className="field-input resize-none leading-relaxed"
                    />
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={loading || !canSubmit}
                    className="btn btn-primary w-full py-4 text-base"
                  >
                    {loading ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send size={18} />
                        Send Project Brief
                      </>
                    )}
                  </button>

                  <p className="text-zinc-500 text-xs text-center">
                    By submitting, you agree that we may contact you about your project.
                    We never share your information with third parties.
                  </p>
                </form>
              </Reveal>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
